export const DASH = "-";

const BLANK_TOKENS = new Set(["", "-1", "XXX", "NA", "N/A", "NULL", "null", "*"]);

export function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  const s = String(value).trim();
  return BLANK_TOKENS.has(s) || BLANK_TOKENS.has(s.toUpperCase());
}

/** Parse a CIBIL numeric string. Returns null for blank / -1 / XXX / NaN. */
export function toNumber(value: unknown): number | null {
  if (isBlank(value)) return null;
  const n = Number(String(value).replace(/,/g, "").trim());
  if (!Number.isFinite(n)) return null;
  if (n < 0) return null;
  return n;
}

/** Parse CIBIL dates like "2026-08-09+05:30". */
export function toDate(value: unknown): Date | null {
  if (isBlank(value)) return null;
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const MONTH_LABELS = MONTHS;

export function monthLabel(key: string): string {
  const [y = "", m = ""] = key.split("-");
  const idx = Number(m) - 1;
  const name = MONTHS[idx] ?? m;
  return `${name} ${y.slice(2)}`;
}

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `₹${Math.round(value).toLocaleString("en-IN")}`;
}

/** Compact Indian money: ₹1.2L / ₹3.4Cr / ₹9,500 */
export function formatCompactINR(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  const abs = Math.abs(value);
  if (abs >= 1e7) return `₹${(value / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(value / 1e5).toFixed(2)}L`;
  return `₹${Math.round(value).toLocaleString("en-IN")}`;
}

export function formatLakh(value: number): number {
  return Number((value / 1e5).toFixed(2));
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return value.toLocaleString("en-IN");
}

export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `${value.toFixed(digits)}%`;
}

export function formatDate(date: Date | null | undefined): string {
  if (!date) return DASH;
  return `${String(date.getDate()).padStart(2, "0")} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export function dash(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  const s = String(value).trim();
  return s === "" ? DASH : s;
}

export function daysBetween(a: Date, b: Date): number {
  return Math.round((a.getTime() - b.getTime()) / 86_400_000);
}

/** riskScore of 1 (NH) means the bureau has no scoreable credit history. */
export const NO_HISTORY_SCORE = 1;

export const NO_HISTORY_EXPLAINER =
  "NH (No History) means CIBIL does not have enough credit history to generate a score for this individual.";
